// src/components/Atelier3/Activite1/CategoryManager.js
import React, { useMemo, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Tags,
  Check,
  X,
  AlertTriangle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { showErrorToast } from "../../../utils/toastUtils";
import ReactDOM from "react-dom";
import {
  CATEGORY_UNSORTED_ID,
  isBuiltinCategory,
  getCategoryColor,
  getCategoryDisplayName,
} from "../../../utils/stakeholderCategories";

const CategoryManager = ({ stakeholders, onAddCategory, onRenameCategory, onDeleteCategory, embedded = false }) => {
  const { t, i18n } = useTranslation();

  const categories = useMemo(
    () => (stakeholders || []).filter((s) => s.isCategory && !s.deleted),
    [stakeholders],
  );
  const partPrenantes = useMemo(
    () => (stakeholders || []).filter((s) => !s.isCategory),
    [stakeholders],
  );

  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reassignTarget, setReassignTarget] = useState("");

  const countFor = (id) => partPrenantes.filter((p) => p.category === id).length;

  const startAdd = () => {
    setNewName("");
    setIsAdding(true);
  };

  const submitAdd = () => {
    const name = newName.trim();
    if (!name) return;
    const exists = categories.some(
      (c) => String(c.name || "").toLowerCase() === name.toLowerCase(),
    );
    if (exists) {
      // Ponytail: duplicate check lives here + in parent handler; cheap, avoid dupes.
      showErrorToast(
        t("workshop3.activity1.categoryManager.errors.duplicate", "Une catégorie porte déjà ce nom"),
      );
      return;
    }
    onAddCategory(name);
    setIsAdding(false);
    setNewName("");
  };

  const startRename = (cat) => {
    setRenamingId(cat.id);
    setRenameValue(getCategoryDisplayName(cat, t, i18n.language));
  };

  const submitRename = () => {
    const name = renameValue.trim();
    if (!name) {
      setRenamingId(null);
      return;
    }
    onRenameCategory(renamingId, name);
    setRenamingId(null);
  };

  const openDelete = (cat) => {
    setReassignTarget("");
    setDeleteTarget(cat);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    const target = reassignTarget || CATEGORY_UNSORTED_ID;
    onDeleteCategory(deleteTarget.id, target);
    setDeleteTarget(null);
  };

  const targetOptions = categories.filter((c) => c.id !== deleteTarget?.id);

  return (
    <div className={embedded ? "divide-y divide-gray-200" : "bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"}>
      <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-medium text-gray-700 flex items-center">
          <Tags size={18} className="mr-2 text-blue-600" />
          {t("workshop3.activity1.categoryManager.title", "Catégories")}
          <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
            {categories.length}
          </span>
        </h3>
        {!isAdding ? (
          <button
            onClick={startAdd}
            className="inline-flex items-center px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            <Plus size={14} className="mr-1" />
            {t("workshop3.activity1.categoryManager.add", "Ajouter une catégorie")}
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitAdd()}
              placeholder={t("workshop3.activity1.categoryManager.namePlaceholder", "Nom de la catégorie")}
              className="px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 min-w-[200px]"
            />
            <button onClick={submitAdd} className="p-1.5 bg-green-600 text-white rounded-md" title={t("common.save")}>
              <Check size={14} />
            </button>
            <button onClick={() => setIsAdding(false)} className="p-1.5 bg-gray-200 text-gray-600 rounded-md hover:bg-gray-300" title={t("common.cancel")}>
              <X size={14} />
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="text-xs uppercase text-slate-500 bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-3 font-medium">{t("workshop3.activity1.categoryManager.columns.name", "Nom")}</th>
              <th className="px-4 py-3 font-medium">{t("workshop3.activity1.categoryManager.columns.usage", "PP concernées")}</th>
              <th className="px-4 py-3 font-medium">{t("workshop3.activity1.categoryManager.columns.builtIn", "Origine")}</th>
              <th className="px-4 py-3 font-medium text-right">{t("workshop3.activity1.categoryManager.columns.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => {
              const count = countFor(cat.id);
              const builtin = isBuiltinCategory(cat.id);
              const isUnsorted = cat.id === CATEGORY_UNSORTED_ID;
              return (
                <tr key={cat.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-2">
                    {renamingId === cat.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          autoFocus
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && submitRename()}
                          className="px-2 py-1 text-xs border border-gray-300 rounded-md min-w-[180px]"
                        />
                        <button onClick={submitRename} className="p-1 text-green-600 hover:bg-green-50 rounded" title={t("common.save")}>
                          <Check size={14} />
                        </button>
                        <button onClick={() => setRenamingId(null)} className="p-1 text-slate-500 hover:bg-slate-100 rounded" title={t("common.cancel")}>
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <span className="font-medium text-slate-800 flex items-center">
                        <span className="w-2.5 h-2.5 rounded-full mr-2" style={{ backgroundColor: getCategoryColor(cat.id) }} />
                        {getCategoryDisplayName(cat, t, i18n.language)}
                        {isUnsorted && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                            {t("workshop3.activity1.categoryManager.unsorted", "Non classé")}
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      count > 0 ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"
                    }`}>
                      {count}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {builtin ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 uppercase tracking-wide">
                        {t("workshop3.activity1.categoryManager.builtIn", "Intégré")}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">
                        {t("workshop3.activity1.categoryManager.custom", "Personnalisée")}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => startRename(cat)}
                      disabled={isUnsorted}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                      title={t("common.edit")}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => openDelete(cat)}
                      disabled={isUnsorted}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                      title={t("common.delete")}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Delete confirm + forced-reassign modal */}
      {deleteTarget &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">
                  {t("workshop3.activity1.categoryManager.deleteTitle", "Supprimer la catégorie « {{name}} »", { name: getCategoryDisplayName(deleteTarget, t, i18n.language) })}
                </h4>
                {isBuiltinCategory(deleteTarget.id) && (
                  <p className="text-xs text-slate-500 mt-1">
                    {t("workshop3.activity1.categoryManager.builtInWarning", "Catégorie prédéfinie du système.")}
                  </p>
                )}
              </div>
            </div>

            {countFor(deleteTarget.id) > 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  {t("workshop3.activity1.categoryManager.usedBody", "{{count}} partie(s) prenante(s) utilisent cette catégorie. Réassignez-les avant de supprimer.", {
                    count: countFor(deleteTarget.id),
                  })}
                </p>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    {t("workshop3.activity1.categoryManager.reassignTo", "Réassigner à")}
                  </label>
                  <select
                    value={reassignTarget}
                    onChange={(e) => setReassignTarget(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">
                      {t("workshop3.activity1.categoryManager.selectTarget", "Choisir une catégorie...")}
                    </option>
                    {targetOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {getCategoryDisplayName(c, t, i18n.language)}
                      </option>
                    ))}
                    <option value={CATEGORY_UNSORTED_ID}>
                      {t("workshop3.activity1.categoryManager.unsorted", "Non classé")}
                    </option>
                  </select>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-600 mb-3">
                {t("workshop3.activity1.categoryManager.unusedBody", "Cette catégorie n'est utilisée par aucune partie prenante.")}
              </p>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md text-slate-700 hover:bg-gray-50"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={confirmDelete}
                disabled={countFor(deleteTarget.id) > 0 && !reassignTarget}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("workshop3.activity1.categoryManager.confirm", "Supprimer")}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default CategoryManager;